import PremiumVersionIcon from "@/components/PremiumVersionIcon";
import ShopSettingDTO from "@/dto/shop/ShopSettingDTO";
import AbnormalOrderRecordType from "@/enum/AbnormalOrderRecordType";
import AbnormalOrderRecordTypeNameMap from "@/map/AbnormalOrderRecordTypeNameMap";
import { setAbnormalCheckHoursAfterOrderShipped, ShopAdvancedSettingDto } from "@/services/api/shop";
import { PremiumFunctionType, usePremiumValidator } from "@/services/PremiumValidator";
import { useGlobalStore } from "@/store/hooks";
import { useRequest } from "ahooks";
import { Checkbox, Form, InputNumber, message, Spin, Typography } from "antd";
import { observer } from "mobx-react-lite";
import React from "react";

interface AbnormalOrderSettingProps {
    shopSetting: ShopSettingDTO;
    shopAdvancedSetting: ShopAdvancedSettingDto;
}

interface AbnormalOrderSettingFormValues {
    abnormalCheckHoursAfterOrderShipped: number;
}

const AbnormalOrderSetting: React.FC<AbnormalOrderSettingProps> = function (props) {
    const { shopSetting, shopAdvancedSetting } = props;
    const { id: shopId } = shopSetting;
    const globalStore = useGlobalStore();
    const { shopAdvancedSettingStore } = globalStore;
    const { validate: premiumValidate } = usePremiumValidator();
    const [form] = Form.useForm<AbnormalOrderSettingFormValues>();

    const currentAbnormalCheckHoursAfterOrderShipped = shopAdvancedSetting.abnormalCheckHoursAfterOrderShipped || 24;

    const { runAsync: handleAbnormalCheckHoursAfterOrderShipped, loading } = useRequest(
        async (hours: number) => {
            const flag = await premiumValidate({
                type: PremiumFunctionType.ABNORMAL_CHECK_HOURS_AFTER_ORDER_SHIPPED,
                shopId,
            });

            if (!flag) {
                form.setFieldsValue({
                    abnormalCheckHoursAfterOrderShipped: currentAbnormalCheckHoursAfterOrderShipped,
                });

                return;
            }

            const setting = await setAbnormalCheckHoursAfterOrderShipped(shopId, hours);

            shopAdvancedSettingStore.setAdvancedSetting(shopId, setting);

            message.success("设置成功");
        },
        {
            manual: true,
            debounceWait: 500,
        },
    );

    return (
        <Spin spinning={loading}>
            <Form
                form={form}
                initialValues={{
                    abnormalCheckHoursAfterOrderShipped: currentAbnormalCheckHoursAfterOrderShipped,
                }}
            >
                <div
                    style={{
                        background: "#fafafa",
                        padding: 12,
                        borderRadius: 4,
                        marginBottom: 24,
                    }}
                >
                    <div className="text-warn fb mb15">
                        异常订单保存时间:标准版期间生成的异常订单保存7天，高级版期间生成的异常订单保持21天
                    </div>

                    <Typography.Title level={5} style={{ fontSize: "15px", marginBottom: "15px" }}>
                        发货前异常订单检测:
                    </Typography.Title>

                    <Checkbox.Group
                        className="mb15"
                        disabled
                        value={[
                            AbnormalOrderRecordType.CONSIGNEE_CHANGED,
                            AbnormalOrderRecordType.SKU_PROPERTIES_MODIFIED,
                            AbnormalOrderRecordType.SELLER_REMARKS_MODIFIED,
                            AbnormalOrderRecordType.REFUNDING_APPLIED,
                            AbnormalOrderRecordType.ORDER_ITEM_PART_QUANTITY_CANCELED,
                            AbnormalOrderRecordType.ORDER_CANCELED,
                            AbnormalOrderRecordType.ORDER_ITEM_CANCELED,
                        ]}
                    >
                        <Checkbox
                            value={AbnormalOrderRecordType.CONSIGNEE_CHANGED}
                            style={{ width: 250, marginLeft: 0 }}
                        >
                            {AbnormalOrderRecordTypeNameMap[AbnormalOrderRecordType.CONSIGNEE_CHANGED]}
                        </Checkbox>
                        <Checkbox
                            value={AbnormalOrderRecordType.SKU_PROPERTIES_MODIFIED}
                            style={{ width: 250, marginLeft: 0 }}
                        >
                            {AbnormalOrderRecordTypeNameMap[AbnormalOrderRecordType.SKU_PROPERTIES_MODIFIED]}
                        </Checkbox>
                        <Checkbox
                            value={AbnormalOrderRecordType.SELLER_REMARKS_MODIFIED}
                            style={{ width: 250, marginLeft: 0 }}
                        >
                            {AbnormalOrderRecordTypeNameMap[AbnormalOrderRecordType.SELLER_REMARKS_MODIFIED]}
                        </Checkbox>
                        <Checkbox
                            value={AbnormalOrderRecordType.REFUNDING_APPLIED}
                            style={{ width: 250, marginLeft: 0 }}
                        >
                            {AbnormalOrderRecordTypeNameMap[AbnormalOrderRecordType.REFUNDING_APPLIED]}
                        </Checkbox>
                        <Checkbox
                            value={AbnormalOrderRecordType.ORDER_ITEM_PART_QUANTITY_CANCELED}
                            style={{ width: 250, marginLeft: 0 }}
                        >
                            {AbnormalOrderRecordTypeNameMap[AbnormalOrderRecordType.ORDER_ITEM_PART_QUANTITY_CANCELED]}
                        </Checkbox>
                        <Checkbox value={AbnormalOrderRecordType.ORDER_CANCELED} style={{ width: 250, marginLeft: 0 }}>
                            {AbnormalOrderRecordTypeNameMap[AbnormalOrderRecordType.ORDER_CANCELED]}
                        </Checkbox>
                        <Checkbox
                            value={AbnormalOrderRecordType.ORDER_ITEM_CANCELED}
                            style={{ width: 250, marginLeft: 0 }}
                        >
                            {AbnormalOrderRecordTypeNameMap[AbnormalOrderRecordType.ORDER_ITEM_CANCELED]}
                        </Checkbox>
                    </Checkbox.Group>

                    <Typography.Title level={5} style={{ fontSize: "15px", marginBottom: "15px" }}>
                        <PremiumVersionIcon className="mr3" />
                        发货后异常订单检测:
                    </Typography.Title>

                    <div className="mb15">
                        发货后的
                        <Form.Item noStyle name="abnormalCheckHoursAfterOrderShipped">
                            <InputNumber
                                min={1}
                                max={168}
                                step={1}
                                precision={0}
                                className="ml3 mr3"
                                onChange={handleAbnormalCheckHoursAfterOrderShipped}
                            />
                        </Form.Item>
                        小时内，订单发生以下变化生成异常。（例：设置48小时，则48小时内监控，48小时后不管）
                    </div>
                    {/* 这里仅仅是用来展示，实际支持搜索的是【AbnormalOrderRecordType】 */}
                    <Checkbox.Group disabled value={[1, 2, 3, 4]}>
                        <Checkbox value={1} style={{ width: 250, marginLeft: 0 }}>
                            发货后改地址<span style={{ fontSize: "68%" }}>（包含在打印后改地址异常内）</span>
                        </Checkbox>
                        <Checkbox value={2} style={{ width: 250, marginLeft: 0 }}>
                            发货后改备注 <span style={{ fontSize: "68%" }}>（包含在打印后改备注内）</span>
                        </Checkbox>
                        <Checkbox value={3} style={{ width: 250, marginLeft: 0 }}>
                            发货后退款 <span style={{ fontSize: "68%" }}>（包含在打印后退款内）</span>
                        </Checkbox>
                        <Checkbox value={4} style={{ width: 250, marginLeft: 0 }}>
                            发货后订单取消
                        </Checkbox>
                    </Checkbox.Group>
                </div>
            </Form>
        </Spin>
    );
};

export default observer(AbnormalOrderSetting);
